import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Search, Download, ChevronDown, ChevronUp, Loader2, AlertTriangle } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
} from 'recharts';

// Yataş Grubu — Dinamik Takip (Overview'un 1. bölümü). Markanın paylaştığı Looker raporunun
// karşılığı: reklam özeti (Tüm / Branding / Performans), kaynak/ortam, kullanıcı özellikleri,
// arama terimleri, açılış sayfası + sayfa yolu, e-ticaret ürün. Her tablo seçilen dönemi bir
// önceki eşit uzunluktaki dönemle karşılaştırır. Kaynaklar: /api/yatas-tracking/{ads,ga,search-terms}.
// Bölüm bileşenleri dışa açık: Performans Panosu (PerformancePanel.js) aynılarını kullanır.

export const API = 'https://api.glowytics.com';

export const PLATFORM_ICONS = {
  google: '/google_ads-512.webp', meta: '/6033716.png', tiktok: '/tik-tokiconss.png',
  pinterest: '/pinterestlogopng.png', criteo: '/CRcriteopng.png', rtb: '/RTBhouseiconPNG.png',
};

// ── Biçim ──────────────────────────────────────────────────────────────────
const nf0 = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
export const nf1 = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const nf2 = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const int = (v) => (v == null || !isFinite(v) ? '—' : nf0.format(v));
export const tl = (v) => (v == null || !isFinite(v) ? '—' : `₺${nf0.format(v)}`);
export const tl2 = (v) => (v == null || !isFinite(v) || v === 0 ? '—' : `₺${nf2.format(v)}`);
export const pct = (v) => (v == null || !isFinite(v) ? '—' : `%${nf1.format(v)}`);
export const x2 = (v) => (v == null || !isFinite(v) || v === 0 ? '—' : `${nf2.format(v)}x`);
export const dur = (s) => {
  if (s == null || !isFinite(s)) return '—';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, '0')}`;
};
export const short = (v) => {
  if (v == null || !isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a >= 999950) return `${nf1.format(v / 1e6)} Mn`; // 999.960 → "1.000,0 Bin" olmasın
  if (a >= 1e3) return `${nf1.format(v / 1e3)} Bin`;
  return nf0.format(v);
};
// Grafik ekseni: kısa, boşluksuz
export const axis = (v) => (Math.abs(v) >= 1e6 ? `${nf0.format(v / 1e6)}Mn` : Math.abs(v) >= 1e3 ? `${nf0.format(v / 1e3)}Bin` : nf0.format(v));
export const delta = (cur, prev) => (prev > 0 && cur != null ? ((cur - prev) / prev) * 100 : null);
export const div = (a, b) => (b > 0 ? a / b : null);

export const toISO = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};
export const trDate = (iso) => (iso ? iso.split('-').reverse().join('.') : '');

export function presetRange(key) {
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  const daysAgo = (k) => { const d = new Date(y); d.setDate(y.getDate() - (k - 1)); return d; };
  switch (key) {
    case 'yesterday': return [toISO(y), toISO(y)];
    case '7d': return [toISO(daysAgo(7)), toISO(y)];
    case '30d': return [toISO(daysAgo(30)), toISO(y)];
    case 'lastMonth': {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return [toISO(s), toISO(e)];
    }
    case 'thisMonth':
    default: {
      // Ayın 1'inde "bu ay" boş kalır → geçen aya düş
      if (today.getDate() === 1) return presetRange('lastMonth');
      return [toISO(new Date(today.getFullYear(), today.getMonth(), 1)), toISO(y)];
    }
  }
}
export const PRESETS = [
  ['thisMonth', 'Bu ay'], ['lastMonth', 'Geçen ay'], ['7d', 'Son 7 gün'], ['30d', 'Son 30 gün'], ['yesterday', 'Dün'],
];

// GA4 değerlerinin Türkçesi
export const DEVICE_TR = { mobile: 'Mobil', desktop: 'Masaüstü', tablet: 'Tablet', 'smart tv': 'Akıllı TV' };
const GENDER_TR = { male: 'Erkek', female: 'Kadın', unknown: 'Bilinmiyor' };
const AUDIENCE_TR = { 'All Users': 'Tüm kullanıcılar', Purchasers: 'Satın alanlar' };
export const trLabel = (map) => (v) => map[v] || map[String(v).toLowerCase()] || (v === '(not set)' ? 'Belirtilmemiş' : v);

// ── Ortak parçalar ───────────────────────────────────────────────────────────
export function Delta({ cur, prev, invert = false, className = '' }) {
  const d = delta(cur, prev);
  if (d == null) return <span className={`text-gray-300 ${className}`}>—</span>;
  const good = invert ? d < 0 : d > 0;
  const flat = Math.abs(d) < 0.05;
  return (
    <span className={`tabular-nums ${flat ? 'text-gray-400' : good ? 'text-emerald-600' : 'text-rose-600'} ${className}`}>
      {flat ? '' : d > 0 ? '▲ ' : '▼ '}%{nf1.format(Math.abs(d))}
    </span>
  );
}

export function Kpi({ label, value, cur, prev, invert, hint }) {
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white px-4 py-3 min-w-0" title={hint}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500 truncate">{label}</div>
      <div className="mt-1 truncate text-[17px] font-semibold tabular-nums text-gray-900 sm:text-xl">{value}</div>
      {prev !== undefined && <div className="mt-0.5 text-xs"><Delta cur={cur} prev={prev} invert={invert} /></div>}
    </div>
  );
}

export function Section({ id, title, subtitle, right, children }) {
  return (
    <section id={id} className="scroll-mt-24 rounded-3xl border border-gray-200/80 bg-white p-4 sm:p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Tabs({ value, onChange, items, size = 'md' }) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-xl bg-gray-100 p-1">
      {items.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)}
          className={`${size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm'} whitespace-nowrap rounded-lg font-medium transition ${value === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function Loading({ h = 160, text }) {
  return (
    <div className="flex items-center justify-center gap-2 text-sm text-gray-400" style={{ height: h }}>
      <Loader2 className="h-4 w-4 animate-spin" /> {text || 'Yükleniyor…'}
    </div>
  );
}
export function ErrorBox({ text }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <AlertTriangle className="h-4 w-4 shrink-0" /> {text}
    </div>
  );
}

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
function downloadCsv(name, columns, rows) {
  const head = columns.map((c) => csvCell(c.label)).join(';');
  const body = rows.map((r) => columns.map((c) => {
    const v = c.csv ? c.csv(r) : c.value(r);
    return csvCell(typeof v === 'number' ? String(v).replace('.', ',') : v);
  }).join(';'));
  const blob = new Blob([`﻿${[head, ...body].join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/**
 * Sıralanabilir, aranabilir tablo. columns: [{ key, label, value(row), fmt?, align?, delta?(row)→[cur,prev], invert? }]
 */
export function DataTable({ columns, rows, initialSort, pageSize = 10, searchKey, csvName, dense, empty = 'Bu dönemde veri yok.' }) {
  const [sort, setSort] = useState(initialSort || { key: columns[1]?.key, dir: 'desc' });
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const s = q.trim().toLocaleLowerCase('tr');
    const base = s && searchKey ? rows.filter((r) => String(searchKey(r)).toLocaleLowerCase('tr').includes(s)) : rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return base;
    return [...base].sort((a, b) => {
      const va = col.value(a); const vb = col.value(b);
      if (typeof va === 'string' || typeof vb === 'string') {
        return sort.dir === 'asc' ? String(va).localeCompare(String(vb), 'tr') : String(vb).localeCompare(String(va), 'tr');
      }
      return sort.dir === 'asc' ? (va ?? -Infinity) - (vb ?? -Infinity) : (vb ?? -Infinity) - (va ?? -Infinity);
    });
  }, [rows, q, sort, columns, searchKey]);
  const shown = open ? filtered : filtered.slice(0, pageSize);
  const toggle = (k) => setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key: k, dir: 'desc' }));

  return (
    <div className="min-w-0">
      {(searchKey || csvName) && (
        <div className="mb-2 flex items-center justify-between gap-2">
          {searchKey ? (
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara…"
                className="w-full rounded-lg border border-gray-200 py-1.5 pl-8 pr-2 text-sm focus:border-blue-300 focus:outline-none" />
            </div>
          ) : <span />}
          {csvName && (
            <button onClick={() => downloadCsv(csvName, columns, filtered)}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
          )}
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className={`w-full ${dense ? 'text-xs' : 'text-[13px]'}`}>
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              {columns.map((c, i) => (
                <th key={c.key} onClick={() => toggle(c.key)}
                  className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 font-medium hover:text-gray-800 ${i === 0 ? 'text-left' : 'text-right'}`}>
                  {c.label}{sort.key === c.key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.length === 0 && (
              <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-gray-400">{empty}</td></tr>
            )}
            {shown.map((r, ri) => (
              <tr key={ri} className="hover:bg-blue-50/30">
                {columns.map((c, i) => {
                  const v = c.value(r);
                  const d = c.delta ? c.delta(r) : null;
                  return (
                    <td key={c.key} className={`px-3 ${dense ? 'py-1.5' : 'py-2'} ${i === 0 ? 'max-w-[360px] text-left text-gray-800' : 'whitespace-nowrap text-right tabular-nums text-gray-700'}`}>
                      {i === 0 ? (c.render ? c.render(r) : <span className="block truncate" title={String(v)}>{v}</span>) : (c.fmt ? c.fmt(v) : v)}
                      {d && <div className="text-[10px] leading-tight"><Delta cur={d[0]} prev={d[1]} invert={c.invert} /></div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > pageSize && (
        <button onClick={() => setOpen((o) => !o)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800">
          {open ? <><ChevronUp className="h-3.5 w-3.5" /> Daralt</> : <><ChevronDown className="h-3.5 w-3.5" /> Tümünü göster ({int(filtered.length)})</>}
        </button>
      )}
    </div>
  );
}

// GA satırı {dims, cur, prev} → düz nesne
export const flat = (rows, labelFn = (d) => d[0]) => (rows || []).map((r) => ({
  label: labelFn(r.dims), dims: r.dims, ...(r.cur || {}),
  _prev: r.prev || {},
}));

// Standart GA kırılım sütunları: oturum, kullanıcı, etkileşim, işlem, ciro, dönüşüm oranı
const gaColumns = (firstLabel, opts = {}) => [
  { key: 'label', label: firstLabel, value: (r) => r.label, render: opts.render },
  { key: 'sessions', label: 'Oturum', value: (r) => r.sessions || 0, fmt: int, delta: (r) => [r.sessions, r._prev.sessions] },
  ...(opts.share ? [{ key: 'share', label: 'Pay', value: (r) => opts.share(r), fmt: pct }] : []),
  ...(opts.compact ? [] : [{ key: 'totalUsers', label: 'Kullanıcı', value: (r) => r.totalUsers || 0, fmt: int }]),
  ...(opts.bounce
    ? [{ key: 'bounceRate', label: 'Hemen çıkma', value: (r) => (r.bounceRate || 0) * 100, fmt: pct, invert: true }]
    : [{ key: 'engagementRate', label: 'Etkileşim', value: (r) => (r.engagementRate || 0) * 100, fmt: pct }]),
  { key: 'transactions', label: 'İşlem', value: (r) => r.transactions || 0, fmt: int, delta: (r) => [r.transactions, r._prev.transactions] },
  { key: 'totalRevenue', label: 'Ciro', value: (r) => r.totalRevenue || 0, fmt: tl, delta: (r) => [r.totalRevenue, r._prev.totalRevenue] },
  ...(opts.compact ? [] : [{ key: 'cr', label: 'Dön. oranı', value: (r) => (r.sessions > 0 ? (r.transactions / r.sessions) * 100 : 0), fmt: (v) => (v ? `%${nf2.format(v)}` : '—') }]),
];

// ── Sayfa ────────────────────────────────────────────────────────────────────
export default function DynamicTracking() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  const [preset, setPreset] = useState('thisMonth');
  const [range, setRange] = useState(() => presetRange('thisMonth'));
  const [ads, setAds] = useState({ loading: true });
  const [ga, setGa] = useState({ loading: true });
  const [st, setSt] = useState({ loading: true });
  const [refreshKey, setRefreshKey] = useState(0);
  const reqRef = useRef(0);

  const load = useCallback((kind, setter, refresh) => {
    const id = reqRef.current;
    setter((s) => ({ ...s, loading: true, error: null }));
    const qs = `start=${range[0]}&end=${range[1]}${refresh ? '&refresh=1' : ''}`;
    fetch(`${API}/api/yatas-tracking/${kind}?${qs}`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        return j;
      })
      .then((j) => { if (id === reqRef.current) setter({ loading: false, data: j }); })
      .catch((e) => { if (id === reqRef.current) setter({ loading: false, error: e.message }); });
  }, [brandId, range]);

  useEffect(() => {
    if (!brandId) return;
    reqRef.current += 1;
    const refresh = refreshKey > 0;
    load('ads', setAds, refresh);
    load('ga', setGa, refresh);
    load('search-terms', setSt, refresh);
  }, [brandId, range, refreshKey, load]);

  const pickPreset = (k) => { setPreset(k); setRange(presetRange(k)); };
  const setCustom = (i, v) => {
    if (!v) return;
    setPreset('custom');
    setRange((r) => { const n = [...r]; n[i] = v; if (n[0] > n[1]) n[1 - i] = v; return n; });
  };

  const rg = ga.data?.range || ads.data?.range;
  const NAV = [
    ['reklam', 'Reklam Özeti'], ['genel', 'Site Geneli'], ['kaynak', 'Kaynak / Ortam'], ['kullanici', 'Kullanıcı Özellikleri'],
    ['arama', 'Arama Terimleri'], ['sayfa', 'Açılış Sayfası & Sayfa Yolu'], ['urun', 'E-ticaret Ürün'],
  ];

  return (
    <div className="mx-auto max-w-[1500px] space-y-4 px-3 pb-16 pt-2 sm:px-5">
      {/* Başlık + tarih */}
      <div className="sticky top-0 z-20 -mx-3 border-b border-gray-200/70 bg-white/90 px-3 py-3 backdrop-blur sm:-mx-5 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Dinamik Takip</h1>
            <p className="text-xs text-gray-500">
              {selectedBrand?.name} · {trDate(range[0])} – {trDate(range[1])}
              {rg && <> · ▲▼ önceki dönem: {trDate(rg.prevStart)} – {trDate(rg.prevEnd)}</>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs value={preset} onChange={pickPreset} items={PRESETS} size="sm" />
            <div className="flex items-center gap-1 text-sm">
              <input type="date" value={range[0]} max={range[1]} onChange={(e) => setCustom(0, e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700" />
              <span className="text-gray-400">–</span>
              <input type="date" value={range[1]} min={range[0]} max={toISO(new Date())} onChange={(e) => setCustom(1, e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700" />
            </div>
            <button onClick={() => setRefreshKey((k) => k + 1)} title="Verileri yeniden çek"
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
              <RefreshCw className={`h-3.5 w-3.5 ${(ga.loading || ads.loading || st.loading) ? 'animate-spin' : ''}`} /> Yenile
            </button>
          </div>
        </div>
        <nav className="mt-2 flex gap-1 overflow-x-auto text-xs">
          {NAV.map(([id, label]) => (
            <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
              className="whitespace-nowrap rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
              {label}
            </a>
          ))}
        </nav>
      </div>

      <AdsSummary ads={ads} ga={ga} />
      <SiteOverview ga={ga} />
      <SourceMedium ga={ga} />
      <UserAttributes ga={ga} />
      <SearchTerms st={st} />
      <Pages ga={ga} />
      <Products ga={ga} />
    </div>
  );
}

// ── 1. Reklam özeti ──────────────────────────────────────────────────────────
const BUCKET_TR = { branding: 'Branding', performance: 'Performans', other: 'Diğer' };

export function aggregateAds(rows, bucket) {
  const t = { cost: 0, impressions: 0, clicks: 0, views: 0 };
  for (const r of rows || []) {
    if (bucket !== 'all' && r.bucket !== bucket) continue;
    t.cost += r.cost; t.impressions += r.impressions; t.clicks += r.clicks; t.views += r.views;
  }
  return t;
}

export function AdsSummary({ ads, ga }) {
  const [tab, setTab] = useState('all');
  const d = ads.data;
  const allBranding = d?.allBranding;
  useEffect(() => { if (allBranding && tab === 'performance') setTab('branding'); }, [allBranding, tab]);

  const cur = useMemo(() => aggregateAds(d?.current, tab), [d, tab]);
  const prev = useMemo(() => aggregateAds(d?.previous, tab), [d, tab]);
  const split = ga.data?.campaignSplit?.[tab];
  const gC = split?.cur || {}; const gP = split?.prev || {};

  const platformRows = useMemo(() => {
    if (!d) return [];
    const pick = (rows) => (rows || []).filter((r) => tab === 'all' || r.bucket === tab);
    const agg = (rows) => {
      const m = new Map();
      for (const r of rows) {
        const x = m.get(r.platform) || { cost: 0, impressions: 0, clicks: 0, views: 0 };
        x.cost += r.cost; x.impressions += r.impressions; x.clicks += r.clicks; x.views += r.views;
        m.set(r.platform, x);
      }
      return m;
    };
    const c = agg(pick(d.current)); const p = agg(pick(d.previous));
    return d.platforms.filter((pl) => c.has(pl.key) || p.has(pl.key)).map((pl) => ({
      key: pl.key, label: pl.label, ...(c.get(pl.key) || { cost: 0, impressions: 0, clicks: 0, views: 0 }), _prev: p.get(pl.key) || {},
    }));
  }, [d, tab]);

  const campaignRows = useMemo(() => {
    if (!d) return [];
    const prevBy = new Map((d.previous || []).map((r) => [`${r.platform}|${r.campaign}`, r]));
    const gaBy = new Map((ga.data?.campaigns || []).map((r) => [String(r.dims[0]).toLowerCase(), r.cur || {}]));
    return (d.current || []).filter((r) => tab === 'all' || r.bucket === tab).map((r) => {
      const g = gaBy.get(String(r.campaign).toLowerCase());
      return { ...r, _prev: prevBy.get(`${r.platform}|${r.campaign}`) || {}, sessions: g?.sessions ?? null, revenue: g?.totalRevenue ?? null };
    });
  }, [d, ga.data, tab]);

  const bucketCost = useMemo(() => {
    if (!d) return null;
    const b = aggregateAds(d.current, 'branding').cost; const p = aggregateAds(d.current, 'performance').cost;
    const all = aggregateAds(d.current, 'all').cost;
    return { branding: b, performance: p, other: Math.max(0, all - b - p), all };
  }, [d]);

  const tabs = allBranding
    ? [['all', 'Tüm Özet'], ['branding', 'Branding Özet']]
    : [['all', 'Tüm Özet'], ['branding', 'Branding Özet'], ['performance', 'Performans Özet']];
  const sessLabel = tab === 'all' ? 'Kampanyalı oturum' : 'Oturum';

  return (
    <Section id="reklam" title="Reklam Özeti"
      subtitle="Harcama, gösterim, tıklama ve izlenme reklam platformlarından; oturum ve ciro GA4'ten (kampanya adıyla eşleşen, son tıklama). Branding/Performans ayrımı kampanya adına göre."
      right={<Tabs value={tab} onChange={setTab} items={tabs} />}>
      {ads.loading && !d ? <Loading /> : ads.error ? <ErrorBox text={ads.error} /> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi label="Harcama" value={tl(cur.cost)} cur={cur.cost} prev={prev.cost} />
            <Kpi label="Gösterim" value={short(cur.impressions)} cur={cur.impressions} prev={prev.impressions} />
            <Kpi label="Tıklama" value={short(cur.clicks)} cur={cur.clicks} prev={prev.clicks} />
            <Kpi label="TO (CTR)" value={pct(div(cur.clicks, cur.impressions) * 100)} cur={div(cur.clicks, cur.impressions)} prev={div(prev.clicks, prev.impressions)} />
            <Kpi label="TBM (CPC)" value={tl2(div(cur.cost, cur.clicks))} cur={div(cur.cost, cur.clicks)} prev={div(prev.cost, prev.clicks)} invert />
            <Kpi label="BGM (CPM)" value={tl2(div(cur.cost, cur.impressions) * 1000)} cur={div(cur.cost, cur.impressions)} prev={div(prev.cost, prev.impressions)} invert />
            <Kpi label="İzlenme" value={short(cur.views)} cur={cur.views} prev={prev.views} hint="Google (bumper dahil), Pinterest ve TikTok video izlenmesi" />
            <Kpi label={sessLabel} value={ga.loading && !split ? '…' : short(gC.sessions)} cur={gC.sessions} prev={gP.sessions} hint="GA4 oturumları, kampanya adıyla" />
            <Kpi label="Oturum başı maliyet" value={tl2(div(cur.cost, gC.sessions))} cur={div(cur.cost, gC.sessions)} prev={div(prev.cost, gP.sessions)} invert />
            <Kpi label="İşlem" value={int(gC.transactions)} cur={gC.transactions} prev={gP.transactions} />
            <Kpi label="Ciro (GA4)" value={tl(gC.totalRevenue)} cur={gC.totalRevenue} prev={gP.totalRevenue} />
            <Kpi label="ROAS" value={x2(div(gC.totalRevenue, cur.cost))} cur={div(gC.totalRevenue, cur.cost)} prev={div(gP.totalRevenue, prev.cost)} />
          </div>

          {tab === 'all' && bucketCost?.all > 0 && (
            <div className="mt-4">
              <div className="mb-1 flex justify-between text-xs text-gray-500"><span>Harcama dağılımı</span><span>{tl(bucketCost.all)}</span></div>
              <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100">
                <div className="bg-indigo-500" style={{ width: `${(bucketCost.branding / bucketCost.all) * 100}%` }} />
                <div className="bg-emerald-500" style={{ width: `${(bucketCost.performance / bucketCost.all) * 100}%` }} />
                <div className="bg-gray-300" style={{ width: `${(bucketCost.other / bucketCost.all) * 100}%` }} />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-4 text-xs text-gray-600">
                <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-indigo-500" />Branding {tl(bucketCost.branding)} · {pct((bucketCost.branding / bucketCost.all) * 100)}</span>
                {!allBranding && <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />Performans {tl(bucketCost.performance)} · {pct((bucketCost.performance / bucketCost.all) * 100)}</span>}
                {bucketCost.other >= 1 && <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-gray-300" />Adında ayrım olmayan {tl(bucketCost.other)}</span>}
              </div>
            </div>
          )}

          <div className="mt-5 space-y-5">
            <div>
              <h3 className="mb-2 text-sm font-medium text-gray-800">Platformlar</h3>
              <DataTable dense rows={platformRows} initialSort={{ key: 'cost', dir: 'desc' }} pageSize={8} columns={[
                { key: 'label', label: 'Platform', value: (r) => r.label,
                  render: (r) => <span className="flex items-center gap-2 whitespace-nowrap"><img src={PLATFORM_ICONS[r.key]} alt="" className="h-4 w-4 object-contain" />{r.label}</span> },
                { key: 'cost', label: 'Harcama', value: (r) => r.cost, fmt: tl, delta: (r) => [r.cost, r._prev.cost] },
                { key: 'share', label: 'Pay', value: (r) => (cur.cost > 0 ? (r.cost / cur.cost) * 100 : 0), fmt: pct },
                { key: 'impressions', label: 'Gösterim', value: (r) => r.impressions, fmt: short, delta: (r) => [r.impressions, r._prev.impressions] },
                { key: 'clicks', label: 'Tıklama', value: (r) => r.clicks, fmt: short, delta: (r) => [r.clicks, r._prev.clicks] },
                { key: 'ctr', label: 'TO', value: (r) => (div(r.clicks, r.impressions) || 0) * 100, fmt: pct },
                { key: 'cpc', label: 'TBM', value: (r) => div(r.cost, r.clicks), fmt: tl2 },
                { key: 'cpm', label: 'BGM', value: (r) => (div(r.cost, r.impressions) || 0) * 1000, fmt: tl2 },
                { key: 'views', label: 'İzlenme', value: (r) => r.views, fmt: (v) => (v ? short(v) : '—') },
              ]} />
            </div>
            <div>
              <h3 className="mb-2 text-sm font-medium text-gray-800">Kampanyalar</h3>
              <DataTable dense rows={campaignRows} initialSort={{ key: 'cost', dir: 'desc' }} pageSize={8}
                searchKey={(r) => r.campaign} csvName={`kampanyalar_${tab}`} columns={[
                  { key: 'campaign', label: 'Kampanya', value: (r) => r.campaign,
                    render: (r) => <span className="flex min-w-0 items-center gap-2"><img src={PLATFORM_ICONS[r.platform]} alt="" className="h-3.5 w-3.5 shrink-0 object-contain" /><span className="truncate" title={r.campaign}>{r.campaign}</span></span> },
                  { key: 'bucket', label: 'Tür', value: (r) => BUCKET_TR[r.bucket] },
                  { key: 'cost', label: 'Harcama', value: (r) => r.cost, fmt: tl, delta: (r) => [r.cost, r._prev.cost] },
                  { key: 'impressions', label: 'Gösterim', value: (r) => r.impressions, fmt: short },
                  { key: 'clicks', label: 'Tıklama', value: (r) => r.clicks, fmt: short },
                  { key: 'ctr', label: 'TO', value: (r) => (div(r.clicks, r.impressions) || 0) * 100, fmt: pct },
                  { key: 'cpc', label: 'TBM', value: (r) => div(r.cost, r.clicks), fmt: tl2 },
                  { key: 'views', label: 'İzlenme', value: (r) => r.views, fmt: (v) => (v ? short(v) : '—') },
                  { key: 'sessions', label: 'Oturum', value: (r) => r.sessions, fmt: (v) => (v == null ? '—' : short(v)) },
                  { key: 'revenue', label: 'Ciro', value: (r) => r.revenue, fmt: (v) => (v == null ? '—' : tl(v)) },
                  { key: 'roas', label: 'ROAS', value: (r) => (r.revenue == null ? null : div(r.revenue, r.cost)), fmt: x2 },
                ]} />
            </div>
          </div>
        </>
      )}
    </Section>
  );
}

// ── 2. Site geneli (GA4) ─────────────────────────────────────────────────────
export function SiteOverview({ ga }) {
  const t = ga.data?.totals?.[0];
  const c = t?.cur || {}; const p = t?.prev || {};
  const daily = useMemo(() => (ga.data?.daily || []).map((r) => ({
    d: `${r.dims[0].slice(6, 8)}.${r.dims[0].slice(4, 6)}`, sessions: r.cur?.sessions || 0, revenue: r.cur?.totalRevenue || 0,
  })), [ga.data]);
  return (
    <Section id="genel" title="Site Geneli" subtitle="GA4, canlı. Tüm kanallar.">
      {ga.loading && !t ? <Loading text="GA4'ten çekiliyor…" /> : ga.error ? <ErrorBox text={ga.error} /> : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:col-span-2 xl:grid-cols-2">
            <Kpi label="Oturum" value={short(c.sessions)} cur={c.sessions} prev={p.sessions} />
            <Kpi label="Kullanıcı" value={short(c.totalUsers)} cur={c.totalUsers} prev={p.totalUsers} />
            <Kpi label="Yeni kullanıcı" value={short(c.newUsers)} cur={c.newUsers} prev={p.newUsers} />
            <Kpi label="Sayfa görüntüleme" value={short(c.screenPageViews)} cur={c.screenPageViews} prev={p.screenPageViews} />
            <Kpi label="Sitede kalma süresi" value={dur(c.averageSessionDuration)} cur={c.averageSessionDuration} prev={p.averageSessionDuration} />
            <Kpi label="Hemen çıkma" value={pct((c.bounceRate || 0) * 100)} cur={c.bounceRate} prev={p.bounceRate} invert />
            <Kpi label="İşlem" value={int(c.transactions)} cur={c.transactions} prev={p.transactions} />
            <Kpi label="Ciro" value={tl(c.totalRevenue)} cur={c.totalRevenue} prev={p.totalRevenue} />
            <Kpi label="Dönüşüm oranı" value={c.sessions ? `%${nf2.format((c.transactions / c.sessions) * 100)}` : '—'} cur={div(c.transactions, c.sessions)} prev={div(p.transactions, p.sessions)} />
            <Kpi label="Sepet ortalaması" value={tl(div(c.totalRevenue, c.transactions))} cur={div(c.totalRevenue, c.transactions)} prev={div(p.totalRevenue, p.transactions)} />
          </div>
          <div className="rounded-2xl border border-gray-200/80 p-3 xl:col-span-3">
            <div className="mb-1 flex items-center gap-4 text-xs text-gray-500">
              <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-blue-500" />Oturum</span>
              <span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500" />Ciro</span>
            </div>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dtSess" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.25} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="d" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} minTickGap={16} />
                  <YAxis yAxisId="s" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} tickFormatter={axis} width={48} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: '#94A3B8' }} tickLine={false} axisLine={false} tickFormatter={(v) => `₺${axis(v)}`} width={60} />
                  <Tooltip formatter={(v, k) => (k === 'revenue' ? [tl(v), 'Ciro'] : [int(v), 'Oturum'])} labelStyle={{ color: '#334155' }} />
                  <Area yAxisId="s" type="monotone" dataKey="sessions" stroke="#3B82F6" strokeWidth={2} fill="url(#dtSess)" />
                  <Line yAxisId="r" type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// ── 3. Kaynak / ortam ────────────────────────────────────────────────────────
export function SourceMedium({ ga }) {
  const rows = useMemo(() => flat(ga.data?.sourceMedium), [ga.data]);
  const total = ga.data?.totals?.[0]?.cur?.sessions || 0;
  return (
    <Section id="kaynak" title="Kaynak / Ortam" subtitle="GA4 oturum kaynağı / ortamı (session source / medium).">
      {ga.loading && !ga.data ? <Loading /> : ga.error ? <ErrorBox text={ga.error} /> : (
        <DataTable rows={rows} initialSort={{ key: 'sessions', dir: 'desc' }} pageSize={15}
          searchKey={(r) => r.label} csvName="kaynak_ortam"
          columns={gaColumns('Kaynak / Ortam', { share: (r) => (total ? (r.sessions / total) * 100 : 0) })} />
      )}
    </Section>
  );
}

// ── 4. Kullanıcı özellikleri ─────────────────────────────────────────────────
export const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#64748B'];

export function ShareBars({ rows, metric = 'sessions', fmt = short }) {
  const total = rows.reduce((a, r) => a + (r[metric] || 0), 0);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const share = total ? ((r[metric] || 0) / total) * 100 : 0;
        return (
          <div key={r.label}>
            <div className="flex justify-between text-xs">
              <span className="text-gray-700">{r.label}</span>
              <span className="tabular-nums text-gray-500">{fmt(r[metric])} · {pct(share)} <Delta cur={r[metric]} prev={r._prev[metric]} className="ml-1" /></span>
            </div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full" style={{ width: `${share}%`, background: PIE_COLORS[i % PIE_COLORS.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function MiniCard({ title, note, children }) {
  return (
    <div className="min-w-0 rounded-2xl border border-gray-200/80 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-gray-800">{title}</h3>
        {note && <span className="text-[11px] text-gray-400">{note}</span>}
      </div>
      {children}
    </div>
  );
}

function Demographic({ rows, title, order, empty }) {
  const known = rows.filter((r) => r.dims[0] !== 'unknown');
  const unknown = rows.find((r) => r.dims[0] === 'unknown');
  const all = rows.reduce((a, r) => a + (r.sessions || 0), 0);
  const sorted = order ? [...known].sort((a, b) => order.indexOf(a.dims[0]) - order.indexOf(b.dims[0])) : known;
  return (
    <MiniCard title={title} note={unknown && all ? `Bilinmeyen ${pct((unknown.sessions / all) * 100)} hariç` : null}>
      {sorted.length ? <ShareBars rows={sorted} /> : <div className="text-sm text-gray-400">{empty || 'GA4 bu dönem için demografik veri vermedi.'}</div>}
    </MiniCard>
  );
}

export function UserAttributes({ ga }) {
  const [metric, setMetric] = useState('sessions');
  const d = ga.data;
  const device = useMemo(() => flat(d?.device, (x) => trLabel(DEVICE_TR)(x[0])), [d]);
  const gender = useMemo(() => flat(d?.gender, (x) => trLabel(GENDER_TR)(x[0])), [d]);
  const age = useMemo(() => flat(d?.age, (x) => x[0]), [d]);
  const city = useMemo(() => flat(d?.city, (x) => trLabel({})(x[0])), [d]);
  const browser = useMemo(() => flat(d?.browser), [d]);
  const audience = useMemo(() => flat(d?.audience, (x) => trLabel(AUDIENCE_TR)(x[0])), [d]);
  const deviceTotal = device.reduce((a, r) => a + (r[metric] || 0), 0);
  const total = d?.totals?.[0]?.cur?.sessions || 0;
  const subNote = d?.sub ? 'Alt marka seçiliyken GA4 cinsiyet ve yaş kırılımı vermiyor (demografi oturum filtresiyle birleşmiyor).' : null;

  return (
    <Section id="kullanici" title="Kullanıcı Özellikleri" subtitle="Cihaz, şehir, cinsiyet, yaş, tarayıcı ve kitle. GA4, canlı. Cinsiyet ve yaş Google sinyallerinden gelir; tanınan kullanıcılar üzerinden."
      right={<Tabs size="sm" value={metric} onChange={setMetric} items={[['sessions', 'Oturum'], ['totalRevenue', 'Ciro']]} />}>
      {ga.loading && !d ? <Loading /> : ga.error ? <ErrorBox text={ga.error} /> : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <MiniCard title="Cihaz">
            <div className="flex items-center gap-4">
              <div className="h-[140px] w-[140px] shrink-0">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={device} dataKey={metric} nameKey="label" innerRadius={42} outerRadius={66} paddingAngle={2} stroke="none">
                      {device.map((r, i) => <Cell key={r.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => (metric === 'sessions' ? int(v) : tl(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="min-w-0 flex-1 space-y-1.5 text-xs">
                {device.map((r, i) => (
                  <div key={r.label} className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-gray-700"><i className="inline-block h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{r.label}</span>
                    <span className="tabular-nums text-gray-500">{pct(deviceTotal ? (r[metric] / deviceTotal) * 100 : 0)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <DataTable dense rows={device} initialSort={{ key: 'sessions', dir: 'desc' }} pageSize={6} columns={[
                { key: 'label', label: 'Cihaz', value: (r) => r.label },
                { key: 'sessions', label: 'Oturum', value: (r) => r.sessions || 0, fmt: short, delta: (r) => [r.sessions, r._prev.sessions] },
                { key: 'cr', label: 'Dön. oranı', value: (r) => (r.sessions ? (r.transactions / r.sessions) * 100 : 0), fmt: (v) => (v ? `%${nf2.format(v)}` : '—') },
                { key: 'totalRevenue', label: 'Ciro', value: (r) => r.totalRevenue || 0, fmt: tl },
              ]} />
            </div>
          </MiniCard>
          <Demographic title="Cinsiyet" empty={subNote} rows={gender.map((r) => ({ ...r, sessions: r[metric] || 0, _prev: { sessions: r._prev[metric] } }))} />
          <Demographic title="Yaş" empty={subNote} order={['18-24', '25-34', '35-44', '45-54', '55-64', '65+']}
            rows={age.map((r) => ({ ...r, sessions: r[metric] || 0, _prev: { sessions: r._prev[metric] } }))} />
          <div className="lg:col-span-3 grid grid-cols-1 gap-4 xl:grid-cols-2">
            <MiniCard title="Şehirler">
              <DataTable dense rows={city} initialSort={{ key: metric, dir: 'desc' }} pageSize={10} searchKey={(r) => r.label} csvName="sehirler"
                columns={gaColumns('Şehir', { compact: true, share: (r) => (total ? (r.sessions / total) * 100 : 0) })} />
            </MiniCard>
            <MiniCard title="Tarayıcı">
              <DataTable dense rows={browser} initialSort={{ key: metric, dir: 'desc' }} pageSize={10} csvName="tarayici"
                columns={gaColumns('Tarayıcı', { compact: true, share: (r) => (total ? (r.sessions / total) * 100 : 0) })} />
            </MiniCard>
          </div>
          <div className="lg:col-span-3">
            <MiniCard title="Kitleler" note="GA4'te tanımlı kitleler; bir kullanıcı birden çok kitlede olabilir">
              <DataTable dense rows={audience} initialSort={{ key: metric, dir: 'desc' }} pageSize={10} searchKey={(r) => r.label} csvName="kitleler"
                columns={gaColumns('Kitle')} />
            </MiniCard>
          </div>
        </div>
      )}
    </Section>
  );
}

// ── 5. Arama terimleri ───────────────────────────────────────────────────────
export function SearchTerms({ st }) {
  const d = st.data;
  const rows = useMemo(() => d?.rows || [], [d]);
  const tot = useMemo(() => rows.reduce((a, r) => ({ cost: a.cost + r.cost, clicks: a.clicks + r.clicks, value: a.value + r.value }), { cost: 0, clicks: 0, value: 0 }), [rows]);
  return (
    <Section id="arama" title="Arama Terimleri"
      subtitle={`Google Ads arama terimleri (Arama + Alışveriş kampanyaları), canlı. Performance Max terimleri Google tarafından ayrıca raporlanmadığı için dahil değil.${d ? ` Dönemde ${int(d.totalTerms)} farklı terim; harcamaya göre ilk ${int(rows.length)} gösteriliyor.` : ''}`}>
      {st.loading && !d ? <Loading text="Google Ads'ten çekiliyor…" /> : st.error ? <ErrorBox text={st.error} /> : (
        <>
          <div className="mb-3 grid grid-cols-3 gap-3 sm:max-w-xl">
            <Kpi label="Harcama (listede)" value={tl(tot.cost)} />
            <Kpi label="Tıklama" value={short(tot.clicks)} />
            <Kpi label="Dönüşüm değeri" value={tl(tot.value)} />
          </div>
          <DataTable rows={rows} initialSort={{ key: 'cost', dir: 'desc' }} pageSize={15} searchKey={(r) => r.term} csvName="arama_terimleri" columns={[
            { key: 'term', label: 'Arama terimi', value: (r) => r.term },
            { key: 'impressions', label: 'Gösterim', value: (r) => r.impressions, fmt: int },
            { key: 'clicks', label: 'Tıklama', value: (r) => r.clicks, fmt: int },
            { key: 'ctr', label: 'TO', value: (r) => (div(r.clicks, r.impressions) || 0) * 100, fmt: pct },
            { key: 'cost', label: 'Harcama', value: (r) => r.cost, fmt: tl },
            { key: 'cpc', label: 'TBM', value: (r) => div(r.cost, r.clicks), fmt: tl2 },
            { key: 'conversions', label: 'Dönüşüm', value: (r) => r.conversions, fmt: (v) => (v ? nf1.format(v) : '—') },
            { key: 'value', label: 'Dön. değeri', value: (r) => r.value, fmt: tl },
            { key: 'roas', label: 'ROAS', value: (r) => div(r.value, r.cost), fmt: x2 },
          ]} />
        </>
      )}
    </Section>
  );
}

// ── 6. Açılış sayfası & sayfa yolu ──────────────────────────────────────────
export function Pages({ ga }) {
  const [tab, setTab] = useState('landing');
  const landing = useMemo(() => flat(ga.data?.landingPage, (x) => (x[0] === '(not set)' ? 'Belirtilmemiş' : x[0] === '' ? '(boş — sayfa görüntülemesi olmayan oturum)' : x[0])), [ga.data]);
  const paths = useMemo(() => flat(ga.data?.pagePath).map((r) => ({ ...r, title: r.dims[1] })), [ga.data]);
  return (
    <Section id="sayfa" title="Açılış Sayfası & Sayfa Yolu"
      subtitle={tab === 'landing' ? 'Oturumun başladığı sayfa. GA4, canlı.' : 'Ziyaret edilen tüm sayfalar, görüntülemeye göre. GA4, canlı.'}
      right={<Tabs value={tab} onChange={setTab} items={[['landing', 'Açılış sayfası'], ['path', 'Sayfa yolu']]} />}>
      {ga.loading && !ga.data ? <Loading /> : ga.error ? <ErrorBox text={ga.error} /> : tab === 'landing' ? (
        <DataTable key="l" rows={landing} initialSort={{ key: 'sessions', dir: 'desc' }} pageSize={15} searchKey={(r) => r.label} csvName="acilis_sayfasi"
          columns={gaColumns('Açılış sayfası', { bounce: true })} />
      ) : (
        <DataTable key="p" rows={paths} initialSort={{ key: 'screenPageViews', dir: 'desc' }} pageSize={15}
          searchKey={(r) => `${r.label} ${r.title}`} csvName="sayfa_yolu" columns={[
            { key: 'label', label: 'Sayfa yolu', value: (r) => r.label,
              render: (r) => <div className="min-w-0"><div className="truncate" title={r.label}>{r.label}</div>{r.title && r.title !== '(not set)' && <div className="truncate text-[11px] text-gray-400" title={r.title}>{r.title}</div>}</div> },
            { key: 'screenPageViews', label: 'Görüntüleme', value: (r) => r.screenPageViews || 0, fmt: int, delta: (r) => [r.screenPageViews, r._prev.screenPageViews] },
            { key: 'totalUsers', label: 'Kullanıcı', value: (r) => r.totalUsers || 0, fmt: int },
            { key: 'vpu', label: 'Kullanıcı başı', value: (r) => div(r.screenPageViews, r.totalUsers), fmt: (v) => (v ? nf2.format(v) : '—') },
            { key: 'eng', label: 'Ort. etkileşim süresi', value: (r) => div(r.userEngagementDuration, r.totalUsers), fmt: dur },
          ]} />
      )}
    </Section>
  );
}

// ── 7. E-ticaret ürün ────────────────────────────────────────────────────────
// Varyant birleştirme: "Palladio Sandalye, Keten Dokulu Kumaş 15116 Koyu Gri" → "Palladio Sandalye"
export const baseName = (s) => String(s || '').split(',')[0].trim();

export function Products({ ga }) {
  const [merge, setMerge] = useState(true);
  const rows = useMemo(() => {
    const src = flat(ga.data?.items).filter((r) => r.label && r.label !== '(not set)');
    if (!merge) return src;
    const m = new Map();
    for (const r of src) {
      const k = baseName(r.label);
      const x = m.get(k) || { label: k, itemsViewed: 0, itemsAddedToCart: 0, itemsPurchased: 0, itemRevenue: 0, _prev: { itemsPurchased: 0, itemRevenue: 0 }, variants: 0 };
      x.itemsViewed += r.itemsViewed || 0; x.itemsAddedToCart += r.itemsAddedToCart || 0;
      x.itemsPurchased += r.itemsPurchased || 0; x.itemRevenue += r.itemRevenue || 0;
      x._prev.itemsPurchased += r._prev.itemsPurchased || 0; x._prev.itemRevenue += r._prev.itemRevenue || 0;
      x.variants += 1;
      m.set(k, x);
    }
    return [...m.values()];
  }, [ga.data, merge]);
  return (
    <Section id="urun" title="E-ticaret Ürün"
      subtitle="Ürün görüntüleme, sepete ekleme ve satın alma. GA4, canlı; ciroya göre ilk 300 ürün."
      right={(
        <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={merge} onChange={(e) => setMerge(e.target.checked)} className="rounded border-gray-300" />
          Varyantları birleştir
        </label>
      )}>
      {ga.loading && !ga.data ? <Loading /> : ga.error ? <ErrorBox text={ga.error} /> : (
        <DataTable rows={rows} initialSort={{ key: 'itemRevenue', dir: 'desc' }} pageSize={15} searchKey={(r) => r.label} csvName="urunler" columns={[
          { key: 'label', label: 'Ürün', value: (r) => r.label,
            render: (r) => <span className="flex min-w-0 items-center gap-2"><span className="truncate" title={r.label}>{r.label}</span>{merge && r.variants > 1 && <span className="shrink-0 rounded bg-gray-100 px-1.5 text-[10px] text-gray-500">{r.variants} varyant</span>}</span> },
          { key: 'itemsViewed', label: 'Görüntülenme', value: (r) => r.itemsViewed || 0, fmt: int },
          { key: 'itemsAddedToCart', label: 'Sepete ekleme', value: (r) => r.itemsAddedToCart || 0, fmt: int },
          { key: 'atc', label: 'Sepete ekleme oranı', value: (r) => (div(r.itemsAddedToCart, r.itemsViewed) || 0) * 100, fmt: pct },
          { key: 'itemsPurchased', label: 'Satış adedi', value: (r) => r.itemsPurchased || 0, fmt: int, delta: (r) => [r.itemsPurchased, r._prev.itemsPurchased] },
          { key: 'itemRevenue', label: 'Ciro', value: (r) => r.itemRevenue || 0, fmt: tl, delta: (r) => [r.itemRevenue, r._prev.itemRevenue] },
          { key: 'avg', label: 'Ort. fiyat', value: (r) => div(r.itemRevenue, r.itemsPurchased), fmt: tl },
        ]} />
      )}
    </Section>
  );
}
